<!-- the app bar; an embedded job app shows its rate/rated tabs here, in the look of the anymatch app around it -->
<mat-toolbar
  color="primary"
  data-testid="matching-toolbar"
  [ngClass.xs]="bottomNav ? 'bottom-nav' : ''"
>
  <mat-toolbar-row class="no-select" fxLayoutAlign="space-between center">
    @if (isTimetableRoute$ | async) {
      @if (!timetableOnly && timetableWaiting) {
        <!-- Explicit tabindex on toolbar controls: stock Safari only tabs to elements with one set. -->
        <button
          mat-button
          (click)="navigateToRatedMatches()"
          aria-label="{{ 'TIMETABLE.back_to_matches' | translate }}"
          tabindex="0"
          data-testid="toolbar-timetable-back-button"
        >
          <mat-icon>arrow_back</mat-icon>
          <span fxHide.lt-sm>{{ 'TIMETABLE.back_to_matches' | translate }}</span>
        </button>
      }
      <div class="mat-subtitle-2" fxLayoutAlign="center center" fxLayoutGap="8px">
        @if (isOverviewRoute$ | async) {
          <mat-icon>event_note</mat-icon>
          <span>{{ 'TIMETABLE.toolbar_title_overview' | translate }}</span>
        } @else {
          <mat-icon>calendar_month</mat-icon>
          <span>{{ 'TIMETABLE.configure_button' | translate }}</span>
        }
      </div>
    } @else {
      <div fxLayoutAlign="space-around center" fxFlex="grow" fxLayoutGap="12px">
        @if (embedded) {
          <!-- the sub-tab bar hides; its tabs and their counts move up here -->
          <button
            class="nav-button"
            mat-button
            routerLink="/matching/rate"
            routerLinkActive="selected"
            tabindex="0"
            data-testid="toolbar-embedded-rate"
          >
            <mat-icon>thumbs_up_down</mat-icon
            ><span
              [matBadge]="unratedCount$ | async"
              [matBadgeOverlap]="false"
              matBadgeColor="accent"
              matBadgePosition="above after"
              matBadgeSize="small"
              >{{ 'TABS.matches' | translate }}</span
            >
          </button>
          <button
            class="nav-button"
            mat-button
            routerLink="/matching/rated"
            routerLinkActive="selected"
            tabindex="0"
            data-testid="toolbar-embedded-rated"
          >
            <mat-icon>check_circle</mat-icon
            ><span
              [matBadge]="ratedCount$ | async"
              [matBadgeOverlap]="false"
              matBadgeColor="accent"
              matBadgePosition="above after"
              matBadgeSize="small"
              >{{ 'TABS.rated' | translate }}</span
            >
          </button>
        } @else if (showBrowser) {
          <button
            class="nav-button"
            [fxFlexOrder]="browserFirst ? 0 : 1"
            mat-button
            routerLink="/browser"
            routerLinkActive="selected"
            tabindex="0"
            data-testid="toolbar-rate-button"
          >
            <mat-icon>search</mat-icon
            ><span data-testid="toolbar-rate-badge">{{
              (stopMode ? 'TABS.all_offers' : 'BROWSER.title') | translate
            }}</span>
          </button>
          <!-- anymatch lists its matches instead of rating them -->
          <button
            (click)="markStoryStarted()"
            class="nav-button"
            mat-button
            [routerLink]="stopMode ? '/matching/rated' : '/matching/rate'"
            [class.selected]="isMatching"
            tabindex="0"
            data-testid="toolbar-rated-button"
          >
            <mat-icon>{{ stopMode ? 'format_list_numbered' : 'thumbs_up_down' }}</mat-icon
            ><span data-testid="toolbar-rated-badge">
              {{ (stopMode ? 'TABS.matches' : 'MATCHING.rate') | translate }}
            </span>
          </button>
        }
      </div>
    }
    <button
      [matMenuTriggerFor]="linksMenu"
      class="menu-trigger"
      data-testid="toolbar-menu-button"
      mat-icon-button
      tabindex="0"
      [attr.aria-label]="'COMMON.menu' | translate"
    >
      <mat-icon>menu</mat-icon>
    </button>
  </mat-toolbar-row>
</mat-toolbar>

<mat-menu #linksMenu="matMenu" data-testid="links-menu">
  @for (link of links$ | async; track link) {
    @if (!link.showWhen || link.showWhen()) {
      <a
        [href]="link.url"
        mat-menu-item
        target="_blank noopener noreferrer"
        [attr.data-testid]="link.icon ? 'icon-link' : 'text-link'"
      >
        @if (link.icon) {
          <mat-icon color="primary">{{ link.icon }}</mat-icon>
        }
        <span>{{ link.text | localized | async }}</span>
      </a>
    }
  }
  @if (session$ | async; as sesh) {
    @if (!sesh.isProject) {
      <a (click)="openSessionInfo(sesh)" mat-menu-item data-testid="menu-session-info-button">
        <mat-icon color="primary">info</mat-icon>
        <span>Messeinfos</span>
      </a>
    }
  }
  @if (canSwitchSession) {
    <a (click)="switchSession()" mat-menu-item data-testid="menu-switch-session-button">
      <mat-icon color="primary">swap_horiz</mat-icon>
      <span>{{ 'HOME.SESSIONS.SWITCH' | translate }}</span>
    </a>
  }
  @let storyDone = storyDone$ | async;
  <a (click)="restartStoryClicked(!!storyDone)" mat-menu-item data-testid="menu-restart-button">
    <mat-icon color="primary">restart_alt</mat-icon>
    <span>{{ 'COMMON.restart' | translate }}</span>
  </a>
  <div mat-menu-item (click)="lang.click(); $event.stopPropagation()">
    <div fxLayout="row" fxLayoutAlign="start center">
      <recrewt-language-select style="margin-left: -28px; margin-top: -12px" #lang />
      <span class="no-pad">{{ 'COMMON.lang' | translate }}</span>
    </div>
  </div>
  <!-- an embedded job app takes light or dark from its fair's app (ThemeService) -->
  @if (!embedded) {
    <div
      mat-menu-item
      (click)="toggleDarkMode(); $event.stopPropagation()"
      tabindex="0"
      data-testid="menu-dark-mode-toggle"
    >
      <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="12px">
        <mat-slide-toggle
          [checked]="(isDark$ | async) === true"
          (click)="$event.stopPropagation()"
          (change)="toggleDarkMode()"
          [attr.aria-label]="'COMMON.dark_mode' | translate"
          tabindex="0"
        />
        <span>{{ 'COMMON.dark_mode' | translate }}</span>
      </div>
    </div>
  }
</mat-menu>
<div class="content">
  <div class="nav-content height-match-parent">
    <router-outlet />
  </div>
</div>

@if (showRestartOverlay) {
  <recrewt-mascot-dialog
    [buttonTemplate]="restartDialogButtons"
    [dismissable]="false"
    [message]="overlayMessage"
    [priority]="mascotPriority.USER_ACTION"
    data-testid="restart-overlay"
  />
}

<ng-template #restartDialogButtons>
  @if (showButtons) {
    <div class="m-t-2 m-b-1 dialog-buttons" data-testid="restart-dialog-buttons">
      <button
        (click)="restartStory()"
        class="m-h-2 rounded-button"
        data-testid="confirm-restart-button"
        mat-raised-button
        tabindex="0"
      >
        <span>{{ 'COMMON.restart' | translate }}!</span>
      </button>
      @if (enoughMarked$ | async) {
        <button
          [disabled]="loading"
          recrewtDownloadRoadmap
          [forceRoadmap]="stopMode"
          (loading)="loading = $event"
          [loading]="loading"
          class="m-h-2 p-h-2 rounded-button"
          color="primary"
          data-testid="download-before-restart-button"
          mat-raised-button
          tabindex="0"
        >
          <span>{{ downloadButtonText | translate }}</span>
        </button>
      } @else if (storyDone$ | async) {
        <button
          class="m-h-2 p-h-2 rounded-button"
          color="primary"
          data-testid="redirect-before-restart-button"
          mat-raised-button
          tabindex="0"
          routerLink="/matching/rated"
          (click)="showRestartOverlay = false"
        >
          <span>{{ 'RATED_MATCHES.create_roadmap' | translate }}</span>
        </button>
      }
      <button
        (click)="abortRestartStory()"
        class="m-h-2 rounded-button"
        data-testid="cancel-restart-button"
        mat-raised-button
        tabindex="0"
      >
        <span>{{ 'COMMON.back' | translate }}</span>
      </button>
    </div>
  }
</ng-template>
